import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ConfirmProvider } from "./components/ConfirmDialog";

import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Confirmation from "@/pages/Confirmation";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Register from "@/pages/Register";
import RegisterVendor from "@/pages/RegisterVendor";
import BecomeHost from "@/pages/BecomeHost";
import Demo from "@/pages/Demo";
import HelpCenter from "@/pages/HelpCenter";
import Shop from "@/pages/Shop";
import Vendre from "@/pages/vendor/Vendre";
import Roadmap from "@/pages/Roadmap";
import HelpArticle from "@/pages/HelpArticle";
import Pricing from "@/pages/Pricing";
import Features from "@/pages/Features";
import FeaturePage from "@/pages/FeaturePage";
import SolutionPage from "@/pages/SolutionPage";
import SolutionsIndex from "@/pages/SolutionsIndex";
import UseCasesIndex from "@/pages/UseCasesIndex";
import UseCase from "@/pages/UseCase";
import PaymentSuccess, { PaymentCancel } from "@/pages/PaymentSuccess";
import LegalMentions from "@/pages/legal/Mentions";
import LegalPrivacy from "@/pages/legal/Privacy";
import LegalCookies from "@/pages/legal/Cookies";
import LegalTerms from "@/pages/legal/Terms";
import PourquoiJunago from "@/pages/PourquoiJunago";
import Contact from "@/pages/Contact";
import InteractiveDemoHost from "@/components/demo/DemoLauncher";
import ComingSoon from "@/pages/ComingSoon";
import { isLandingMode } from "@/lib/landingMode";

// ── Séparation Production / Preview par domaine (runtime) ─────────────
// Sur le domaine public de production (junago.io), on n'affiche QUE la landing
// « Coming Soon » + les pages légales. Le Preview et le local gardent l'app
// complète inchangée (voir lib/landingMode.js).
function PublicLandingApp() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/legal/mentions" element={<LegalMentions />} />
            <Route path="/legal/privacy" element={<LegalPrivacy />} />
            <Route path="/legal/cookies" element={<LegalCookies />} />
            <Route path="/legal/cgv" element={<LegalTerms />} />
            <Route path="*" element={<ComingSoon />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

const AdminApp = React.lazy(() => import("@/admin/AdminApp"));
import ClientBookings from "@/pages/client/Bookings";
import Contract from "@/pages/Contract";
import QuotePublic from "@/pages/QuotePublic";
import Inspection from "@/pages/Inspection";
import DepartureFlow from "@/pages/vendor/DepartureFlow";
import ReturnFlow from "@/pages/vendor/ReturnFlow";

import VendorPilotage from "@/pages/vendor/Pilotage";
import VendorParametrages from "@/pages/vendor/Parametrages";
import VendorVehicles from "@/pages/vendor/Vehicles";
import VendorBookings from "@/pages/vendor/Bookings";
import VendorRevenue from "@/pages/vendor/Revenue";
import VendorClients from "@/pages/vendor/Clients";
import StripeOnboarding from "@/pages/vendor/StripeOnboarding";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function BookingDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/vendor/pilotage?tab=bookings&booking=${id}`} replace />;
}

function VehicleDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/vendor/pilotage?tab=vehicles&vehicle=${id}`} replace />;
}

const LEGACY_CASE_SLUGS = { "paris-luxury-cars-gestion-centralisee": "location-premium-gestion-centralisee" };
function LegacyCaseRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/cas-d-usage/${LEGACY_CASE_SLUGS[slug] || slug}`} replace />;
}

function App() {
  if (isLandingMode()) {
    return <PublicLandingApp />;
  }
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <InteractiveDemoHost />
          <Routes>
            <Route path="/" element={<BecomeHost />} />
            <Route path="/devenir-loueur" element={<Navigate to="/" replace />} />
            <Route path="/search" element={<Navigate to="/" replace />} />
            <Route path="/vehicles" element={<Navigate to="/" replace />} />
            <Route path="/comment-ca-marche" element={<Navigate to="/assistance" replace />} />
            <Route path="/tarifs" element={<Pricing />} />
            <Route path="/fonctionnalites" element={<Features />} />
            <Route path="/fonctionnalites/:slug" element={<FeaturePage />} />
            <Route path="/solutions" element={<SolutionsIndex />} />
            <Route path="/solutions/:slug" element={<SolutionPage />} />
            <Route path="/cas-d-usage" element={<UseCasesIndex />} />
            <Route path="/cas-d-usage/:slug" element={<UseCase />} />
            <Route path="/etudes-de-cas" element={<Navigate to="/cas-d-usage" replace />} />
            <Route path="/etudes-de-cas/:slug" element={<LegacyCaseRedirect />} />
            <Route path="/pourquoi-junago" element={<PourquoiJunago />} />
            <Route path="/contact" element={<Contact />} />
            <Route
              path="/admin/*"
              element={
                <React.Suspense fallback={<div className="min-h-screen bg-slate-50" />}>
                  <AdminApp />
                </React.Suspense>
              }
            />
            <Route path="/confirmation/:id" element={<Confirmation />} />
            <Route path="/login" element={<Login />} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/inscription-loueur" element={<RegisterVendor />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/p/:slug" element={<Shop />} />
            <Route path="/assistance" element={<HelpCenter />} />
            <Route path="/assistance/roadmap" element={<Roadmap />} />
            <Route path="/assistance/article/:slug" element={<HelpArticle />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/legal/mentions" element={<LegalMentions />} />
            <Route path="/legal/privacy" element={<LegalPrivacy />} />
            <Route path="/legal/cookies" element={<LegalCookies />} />
            <Route path="/legal/cgv" element={<LegalTerms />} />
            <Route
              path="/mes-reservations"
              element={
                <ProtectedRoute>
                  <ClientBookings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/vendor/stripe/onboarding"
              element={
                <ProtectedRoute>
                  <StripeOnboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendor/dashboard"
              element={<Navigate to="/vendor/pilotage" replace />}
            />
            <Route
              path="/vendor/calendar"
              element={<Navigate to="/vendor/pilotage?tab=calendar" replace />}
            />
            <Route
              path="/vendor/vehicles"
              element={<Navigate to="/vendor/pilotage?tab=vehicles" replace />}
            />
            <Route
              path="/vendor/bookings"
              element={<Navigate to="/vendor/pilotage?tab=bookings" replace />}
            />
            <Route
              path="/vendor/clients"
              element={<Navigate to="/vendor/pilotage?tab=clients" replace />}
            />
            <Route
              path="/vendor/revenue"
              element={<Navigate to="/vendor/pilotage?tab=revenue" replace />}
            />
            <Route
              path="/vendor/pilotage"
              element={
                <ProtectedRoute>
                  <VendorPilotage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendor/parametrages"
              element={
                <ProtectedRoute>
                  <VendorParametrages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendor/vendre"
              element={
                <ProtectedRoute>
                  <Vendre />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendor/vehicles/standalone"
              element={
                <ProtectedRoute>
                  <VendorVehicles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendor/vehicles/:id"
              element={
                <ProtectedRoute>
                  <VehicleDetailRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendor/bookings/:id"
              element={
                <ProtectedRoute>
                  <BookingDetailRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendor/depart/:bookingId"
              element={
                <ProtectedRoute>
                  <DepartureFlow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendor/retour/:bookingId"
              element={
                <ProtectedRoute>
                  <ReturnFlow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contract/:bookingId"
              element={
                <ProtectedRoute>
                  <Contract />
                </ProtectedRoute>
              }
            />
            <Route path="/quote/:quoteId" element={<QuotePublic />} />
            <Route
              path="/inspection/:bookingId/:type"
              element={
                <ProtectedRoute>
                  <Inspection />
                </ProtectedRoute>
              }
            />
          </Routes>
          <ConfirmProvider />
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
